import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { CalendarDays, CheckCircle2, Clock, Loader2, MapPin, Microscope, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { useSEO } from "@/lib/useSEO";
import { THC } from "@/lib/tankHealthCheck";
import { CampaignHeader } from "@/components/tank-health-check/CampaignHeader";
import { CampaignFooter } from "@/components/tank-health-check/CampaignFooter";

const STEPS = [
  "Pristine Water Systems attends your scheduled Tank Water Health Check.",
  "The technician completes the onsite assessment.",
  "Your AWARE water sample is collected during the same visit.",
  "AWARE completes the laboratory testing and prepares your results.",
  "You receive your scientific results, easy-to-understand summary and recommendations if water correction is needed.",
];

/** Campaign purchase conversion. The server hands `analytics` back ONCE
    per paid order (it claims the event itself), so a refresh, a revisit
    or a webhook/poll race can never produce a second Purchase. The
    sessionStorage key is only a belt-and-braces client guard. No
    Schedule is fired here — that belongs to the Health Check booking. */
function pushPurchase(data) {
  const a = data?.analytics;
  if (!a?.transaction_id) return;
  const key = `aware_lab_purchase:${a.transaction_id}`;
  try {
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
  } catch { /* private mode — the server claim is the real guard */ }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "aware_lab_purchase",
    transaction_id: a.transaction_id,
    value: a.value,
    currency: a.currency,
    test_id: a.test_id,
    test_name: a.test_name,
    campaign_source: a.campaign_source,
  });
}

/** Campaign-specific confirmation for a lab test bought through the
    Tank Water Health Check offer — no kit, no sample drop-off. */
export default function TankWaterHealthCheckLabConfirmed() {
  const sessionId = new URLSearchParams(useLocation().search).get("session_id") || "";
  const [view, setView] = useState(null);
  const [state, setState] = useState("loading");

  useSEO({ title: "Your AWARE lab test is booked", noindex: true, includeBaseline: false });

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let cancelled = false;
    (async () => {
      // Confirm payment first (this is also the fallback that marks the
      // booking paid if the Stripe webhook is slow), then load the
      // campaign confirmation details.
      for (let attempt = 0; attempt < 5 && !cancelled; attempt += 1) {
        try { await api.get(`/payments/status/${sessionId}`); } catch { /* keep polling */ }
        try {
          const { data } = await api.get("/tank-water-health-check/lab-confirmation",
            { params: { session_id: sessionId } });
          // Push BEFORE the cancellation guard: the server one-shots the
          // analytics payload, so a StrictMode double-mount would otherwise
          // consume the only chance to fire it. dataLayer is global, so
          // firing after unmount is harmless.
          if (data?.paid) pushPurchase(data);
          if (cancelled) return;
          if (data?.paid) { setView(data); setState("ok"); return; }
          setView(data);
        } catch { /* not visible yet */ }
        await new Promise((r) => setTimeout(r, 2000));
      }
      if (!cancelled) setState("pending");
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  const hc = view?.health_check;
  const order = view?.lab_order;

  return (
    <div className="min-h-screen bg-white" data-testid="thc-lab-confirmed-page">
      <CampaignHeader onBook={() => { window.location.href = "/tank-water-health-check#book"; }} />
      <main className="mx-auto w-full max-w-[1440px] px-[18px] md:px-8 py-10 md:py-14">
        {state === "loading" && (
          <p className="flex items-center gap-2 text-base" style={{ color: THC.muted }}>
            <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" /> Confirming your payment…
          </p>
        )}

        {state === "error" && (
          <p className="text-base" data-testid="thc-lab-confirm-error" style={{ color: THC.body }}>
            We couldn’t find this checkout. Please contact AWARE Water Testing on (07) 5327 3473.
          </p>
        )}

        {state === "pending" && (
          <div className="max-w-[720px] rounded-[14px] border p-6" data-testid="thc-lab-confirm-pending"
            style={{ borderColor: THC.border, backgroundColor: THC.faint }}>
            <h1 className="font-extrabold uppercase text-[26px] md:text-[32px] leading-[1.05]"
              style={{ color: THC.navy }}>
              WE’RE STILL CONFIRMING YOUR PAYMENT
            </h1>
            <p className="mt-2 text-base" style={{ color: THC.body }}>
              This can take a few moments. You’ll receive an email confirmation as soon as it
              clears — no further action is needed.
            </p>
          </div>
        )}

        {state === "ok" && (
          <div className="max-w-[900px]">
            <CheckCircle2 className="w-[60px] h-[60px]" style={{ color: THC.blue }} aria-hidden="true" />
            <h1 className="mt-3 font-extrabold uppercase text-[29px] md:text-[40px] leading-[1.03]"
              style={{ color: THC.navy }} data-testid="thc-lab-confirm-heading">
              YOUR AWARE LAB TEST IS BOOKED
            </h1>
            <p className="mt-3 text-base md:text-lg leading-[1.5] max-w-[760px]" style={{ color: THC.body }}>
              Your discounted AWARE laboratory test has been added to your Tank Water Health Check.
              No sample kit is required. The attending Pristine Water Systems technician will
              collect your water sample during your scheduled Tank Water Health Check.
            </p>

            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="thc-lab-confirm-summary">
              <Fact icon={MapPin} label="PROPERTY" value={hc?.property_address} testId="thc-lab-confirm-address" />
              <Fact icon={CalendarDays} label="HEALTH CHECK"
                value={[hc?.date_display, hc?.arrival_window_display].filter(Boolean).join(" · ")}
                testId="thc-lab-confirm-visit" />
              <Fact icon={Microscope} label="LAB TEST" value={order?.test_name} testId="thc-lab-confirm-test" />
              <Fact icon={Clock} label="AMOUNT PAID"
                value={order?.amount != null ? `$${Number(order.amount).toFixed(2)}` : "—"}
                testId="thc-lab-confirm-amount" />
            </div>

            <div className="mt-6 rounded-[14px] border p-5" data-testid="thc-lab-confirm-collection"
              style={{ borderColor: THC.blue, backgroundColor: THC.light }}>
              <div className="flex items-start gap-3">
                <ShieldCheck className="w-6 h-6 shrink-0" style={{ color: THC.blue }} aria-hidden="true" />
                <div>
                  <h2 className="font-extrabold uppercase text-base md:text-lg" style={{ color: THC.navy }}>
                    AWARE SAMPLE COLLECTION INCLUDED
                  </h2>
                  <p className="mt-1 text-base" style={{ color: THC.body }}>
                    Your sample is scheduled to be collected during your Health Check — no
                    separate sample drop-off is required.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-7" data-testid="thc-lab-confirm-next">
              <h2 className="font-extrabold uppercase text-base md:text-lg" style={{ color: THC.navy }}>
                WHAT HAPPENS NEXT
              </h2>
              <ol className="mt-3 space-y-2">
                {STEPS.map((s, i) => (
                  <li key={s} className="flex items-start gap-3 text-base" style={{ color: THC.body }}>
                    <span className="shrink-0 w-6 h-6 rounded-full grid place-items-center text-xs font-bold text-white"
                      style={{ backgroundColor: THC.blue }}>{i + 1}</span>
                    {s}
                  </li>
                ))}
              </ol>
            </div>

            <p className="mt-6 text-sm" style={{ color: THC.muted }} data-testid="thc-lab-confirm-reference">
              Lab order {order?.reference} · Health Check {hc?.reference} — the Health Check is
              conducted by Pristine Water Systems; laboratory analysis and reporting are carried
              out by AWARE Water Testing.
            </p>
          </div>
        )}
      </main>
      <CampaignFooter />
    </div>
  );
}

function Fact({ icon: Icon, label, value, testId }) {
  return (
    <div className="rounded-[12px] border p-4" style={{ borderColor: THC.border }} data-testid={testId}>
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide"
        style={{ color: THC.muted }}>
        <Icon className="w-4 h-4" style={{ color: THC.blue }} aria-hidden="true" /> {label}
      </div>
      <div className="mt-1 text-base font-bold leading-snug" style={{ color: THC.navy }}>
        {value || "—"}
      </div>
    </div>
  );
}
